import {useFormik} from 'formik';
import * as Yup from 'yup';
import {AxiosError} from 'axios';
import {EditCategoryDTO, useCategoryById} from '../../../entities';
import {useSchemes} from '../../../shared';

interface UseEditCategoryFormProps {
  setOpen: (open: boolean) => void;
  categoryId: string | null;
}

type EditFormValues = Pick<EditCategoryDTO, 'name' | 'order' | 'imageFileName' | 'imageUploadId'>;

export const useEditCategoryForm = ({setOpen, categoryId}: UseEditCategoryFormProps) => {
  const {getCategory, updateCategoryMutation} = useCategoryById(categoryId || '');
  const {nameSchema, orderSchema} = useSchemes();

  const onSubmit = async (values: EditFormValues) => {
    if (!categoryId) return;
    const hasNewUpload = Boolean(values.imageUploadId);
    try {
      await updateCategoryMutation.mutateAsync({
        categoryId,
        name: values.name,
        order: values.order,
        imageFileName: hasNewUpload ? null : values.imageFileName ?? null,
        imageUploadId: hasNewUpload ? values.imageUploadId : null,
      });
      setOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const formik = useFormik<EditFormValues>({
    initialValues: {
      name: getCategory.data?.name || '',
      order: getCategory.data?.order || 0,
      imageFileName: getCategory.data?.imageFileName ?? null,
      imageUploadId: null,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: nameSchema,
      order: orderSchema,
      imageFileName: Yup.string().nullable(),
      imageUploadId: Yup.string().nullable(),
    }),
    onSubmit,
  });

  return {
    values: formik.values,
    errors: formik.errors,
    touched: formik.touched,
    setFieldValue: formik.setFieldValue,
    setFieldTouched: formik.setFieldTouched,
    handleChange: formik.handleChange,
    handleBlur: formik.handleBlur,
    resetForm: formik.resetForm,
    submitForm: formik.submitForm,
    isLoading: updateCategoryMutation.isPending,
    submitError: updateCategoryMutation.error
      ? (updateCategoryMutation.error as AxiosError<ApiError>).response?.data
      : undefined,
    category: getCategory.data,
  };
};
